import { CodedProperty, fluentValidator, stringArraysEqual, ValidationResult } from "sprint-common-web";

import { isTrailerVersionType } from "../../../../common/types/TrailerContentVersion";
import {
    ContentAssociationMovieDetails,
    ContentAssociationMovieVersionDetails
} from "../types/ContentAssociationDetails";

export const createContentAssociationMovieItemValidator = () => {
    const validateMovie = (content: ContentAssociationMovieDetails): ValidationResult => {
        return fluentValidator<ContentAssociationMovieDetails>()
            .when((movie) => !!movie.buzz?.isArchived)
            .addWarning("Movie is archived in Buzz")
            .when((movie) => !!movie.buzz?.isDeleted)
            .addWarning("Movie is deleted in Buzz")
            .validate(content);
    };

    const validateVersion = (
        versionToValidate: ContentAssociationMovieVersionDetails,
        allVersions: ContentAssociationMovieVersionDetails[]
    ): ValidationResult => {
        return fluentValidator<ContentAssociationMovieVersionDetails>()
            .when((movieVersion) => {
                return !!movieVersion.buzzVersion?.isArchived;
            })
            .addError("Version is archived")
            .when((movieVersion) => !!movieVersion.buzzVersion?.notes && (movieVersion.brandings ?? []).length < 1)
            .addWarning((movieVersion) => movieVersion.buzzVersion?.notes ?? "")
            .when(isMovieVersionOrphaned)
            .addWarning("Version is orphaned")
            .when((movieVersion) => isDuplicated(allVersions, movieVersion))
            .addWarning((movieVersion) => movieVersion.buzzVersion?.name ?? "Version is duplicated")
            .validate(versionToValidate);
    };

    const isMatchingVersion = (
        versionToValidate: ContentAssociationMovieVersionDetails,
        selectedVersions: ContentAssociationMovieVersionDetails[]
    ) => {
        return selectedVersions.some((version) => doesVersionMatch(versionToValidate, version));
    };
    const doesVersionMatch = (
        movieItem: ContentAssociationMovieVersionDetails,
        secondItem: ContentAssociationMovieVersionDetails
    ): boolean => {
        if (isTrailerVersionType(movieItem.type) && isTrailerVersionType(secondItem.type)) {
            return true;
        }

        return movieItem.type.id === secondItem.type.id && movieItem.brandings?.length === secondItem.brandings?.length;
    };

    return {
        isMatchingVersion,
        validateMovie,
        validateVersion
    };
};

const isMovieVersionOrphaned = (versionToValidate: ContentAssociationMovieVersionDetails): boolean => {
    return (
        !versionToValidate.buzzVersion?.id &&
        !versionToValidate.shreqVersion?.id &&
        !isTrailerVersionType(versionToValidate.type)
    );
};

const isDuplicated = (
    movieVersions: ContentAssociationMovieVersionDetails[],
    movieVersion: ContentAssociationMovieVersionDetails
) => {
    return (
        movieVersions.filter(
            (version) =>
                version.type.id === movieVersion.type.id &&
                version.distributor.id === movieVersion.distributor.id &&
                brandingsEqual(movieVersion.brandings, version.brandings)
        ).length > 1
    );
};

const brandingsEqual = (brandings1?: CodedProperty[], brandings2?: CodedProperty[]) => {
    const brandings1Ids = brandings1?.map((brand: CodedProperty) => brand.id) ?? [];
    const brandings2Ids = brandings2?.map((brand: CodedProperty) => brand.id) ?? [];

    return stringArraysEqual(brandings1Ids, brandings2Ids);
};
